import { useEffect, useState, useCallback } from "react";
import { Users } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

function relTime(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function AdminMembers() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/members");
      setData(data);
    } catch {
      toast.error("Failed to load members");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-slate-500 text-sm">Loading members…</p>;

  return (
    <div data-testid="admin-members" className="max-w-5xl">
      <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mb-6 max-w-md">
        <div className="bg-charcoal p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Active members</p>
          <p className="font-display font-extrabold text-white text-3xl mt-1" data-testid="members-active-count">{data.counts.active}</p>
        </div>
        <div className="bg-charcoal p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Pending payment</p>
          <p className="font-display font-extrabold text-amber-300 text-3xl mt-1" data-testid="members-pending-count">{data.counts.pending_payment}</p>
        </div>
      </div>

      {data.members.length === 0 ? (
        <div className="bg-charcoal border border-white/10 p-10 text-center" data-testid="members-empty">
          <Users size={32} className="text-slate-600 mx-auto" />
          <p className="text-slate-400 text-sm mt-3">No members yet. Enrolled applicants appear here.</p>
        </div>
      ) : (
        <div className="border border-white/10 overflow-x-auto" data-testid="members-table">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="bg-charcoal text-left">
                {["Member", "Enrolled", "Plan", "Progress", "SOS", "Last activity"].map((h) => (
                  <th key={h} className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.email} data-testid={`member-row-${m.email.split("@")[0]}`} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3.5">
                    <p className="text-white font-medium flex items-center gap-2">
                      {m.name || m.email}
                      {m.is_test && <span className="font-mono text-[9px] uppercase tracking-widest text-violet-300 border border-violet-400/40 bg-violet-400/10 px-1.5 py-0.5">test</span>}
                    </p>
                    <p className="text-slate-500 text-xs">{m.business_name || m.trade || m.email}</p>
                  </td>
                  <td className="px-4 py-3.5 text-slate-400 font-mono text-xs">
                    {m.enrolled_at ? new Date(m.enrolled_at).toLocaleDateString() : <span className="text-amber-300">awaiting payment</span>}
                  </td>
                  <td className="px-4 py-3.5">
                    {m.plan ? (
                      <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                        m.payment_status === "past_due" ? "text-red-400 border-red-500/40 bg-red-500/10" : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                      }`}>
                        {m.plan === "full" ? "Paid in full" : `Plan ${m.installments}`}{m.payment_status === "past_due" ? " — past due" : ""}
                      </span>
                    ) : <span className="text-slate-600 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3.5 w-44">
                    <div className="flex items-center gap-2.5">
                      <div className="flex-1 h-1.5 bg-navy overflow-hidden">
                        <div className="h-full bg-safety transition-all" style={{ width: `${m.pct}%` }} />
                      </div>
                      <span className="font-mono text-xs text-slate-400 w-9" data-testid={`member-progress-${m.email.split("@")[0]}`}>{m.pct}%</span>
                    </div>
                    <p className="font-mono text-[9px] text-slate-600 mt-1">{m.completed}/{m.total_items} items</p>
                  </td>
                  <td className="px-4 py-3.5 text-slate-400 font-mono text-xs">{m.open_tickets || "—"}</td>
                  <td className="px-4 py-3.5 text-slate-400 text-xs">{relTime(m.last_activity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
